import { useState } from 'react';
import { endDateForWorkingDays, isWorkingDay, nextWorkingDay, workingDaysBetween } from '@rundown/domain';
import { useHolidays } from '../calendar/useHolidays';
import { cx } from '../../lib/cx';
import { sprintDate } from './sprintMeta';
import { Pencil } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SprintState } from '@rundown/types';
import { Button, Modal } from '../../components';
import { deleteSprint, editSprint, invalidatePlanning, sprintsQueryOptions, unlockSprint } from './api';

/**
 * Whole days between two ISO dates, in UTC.
 *
 * UTC on purpose, and it is not a detail: a sprint "starts on the 21st" in
 * every timezone at once, so parsing these as local dates would make the shift
 * come out a day wrong for anyone west of Greenwich on the wrong side of a
 * DST boundary. Mirrors `daysBetween` in the API's sprint-dates.ts.
 */
function daysBetween(from: string, to: string): number {
  const day = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / day);
}

/**
 * The minimal shape this dialog edits. Deliberately not `DeliverySprint` or
 * `TimelineSprint`: it is mounted from both the Sprints timeline and a Planning
 * column, which hold different (and differently-shaped) sprint objects, and
 * naming the four fields it actually touches is what lets one dialog serve both
 * without either screen converting anything.
 */
export interface EditableSprint {
  readonly id: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
}

/**
 * Editing a sprint: its name, its dates, undoing a Start, deleting one that
 * never ran.
 *
 * This is now the ONLY way to move a sprint. There used to be a Delay control
 * beside it on each planning column — pick 1 day, 3 days or a week, and every
 * later sprint moved with it — which was a second, narrower way to do what
 * this dialog does, from a menu that could not say which sprints it was about
 * to move or what dates they would land on. Setting the end date here and
 * leaving the cascade ticked is the same act, named in dates rather than in
 * days, with the affected sprints listed before anything happens.
 *
 * Behind `workspace.admin`, because every one of these rewrites something a
 * client has already been shown. Nothing here is offered on a released sprint
 * — the server refuses all three, and a closed week is the one thing in this
 * product that genuinely does not move.
 */
export function SprintAdmin({
  workspaceId,
  sprint,
  state,
  trigger = 'button',
}: {
  workspaceId: string;
  sprint: EditableSprint;
  state: SprintState;
  /**
   * The Sprints screen has room for a labelled button; a Planning column header
   * is 210px wide and has room for a glyph. Same dialog either way.
   */
  trigger?: 'button' | 'icon';
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(sprint.name);
  const [startsOn, setStartsOn] = useState(sprint.startsOn);
  const [endsOn, setEndsOn] = useState(sprint.endsOn);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Defaults ON. The team runs one sprint at a time, so a date that moves is
  // almost always a date the following weeks have to move behind — and the
  // alternative default silently leaves the cadence overlapping.
  const [cascade, setCascade] = useState(true);

  // The sibling sprints, only so this dialog can say what a cascade would
  // actually do. Cached and shared with every other consumer of the list.
  const siblings = useQuery({ ...sprintsQueryOptions(workspaceId), enabled: open });

  const done = async () => {
    await invalidatePlanning(queryClient, workspaceId);
    setOpen(false);
    setConfirmDelete(false);
  };

  const edit = useMutation({
    mutationFn: () =>
      editSprint(workspaceId, sprint.id, {
        name: name.trim(),
        startsOn,
        endsOn,
        cascade: shiftDays !== 0 && cascade,
      }),
    onSuccess: done,
  });
  const unlock = useMutation({
    mutationFn: () => unlockSprint(workspaceId, sprint.id),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => deleteSprint(workspaceId, sprint.id),
    onSuccess: done,
  });

  const changed = name.trim() !== sprint.name || startsOn !== sprint.startsOn || endsOn !== sprint.endsOn;
  const datesValid = name.trim().length > 0 && endsOn >= startsOn;

  const holidays = useHolidays(workspaceId);
  const workingDays = datesValid ? workingDaysBetween(startsOn, endsOn, holidays.dates) : 0;
  const lostDays = datesValid ? holidays.within(startsOn, endsOn) : [];
  const startsOnIsNotAWorkingDay = startsOn !== '' && !isWorkingDay(startsOn, holidays.dates);
  const error = edit.error ?? unlock.error ?? remove.error;

  /**
   * How far the later sprints would move, and which ones.
   *
   * The END date decides it, not the start — see UpdateSprintSchema. Moving a
   * sprint wholesale shifts both by the same amount so either would do, but
   * EXTENDING one moves only its end, and the weeks after it have to move by
   * that much or they overlap the sprint that just grew.
   */
  const shiftDays = daysBetween(sprint.endsOn, endsOn);
  const mine = (siblings.data ?? []).find((row) => row.id === sprint.id);
  const later =
    mine === undefined
      ? []
      : (siblings.data ?? []).filter((row) => row.sortIndex > mine.sortIndex && row.state === 'planning');

  return (
    <>
      {trigger === 'icon' ? (
        /* A glyph, not a word: the column header's first job is the sprint's
           name, and two accent-coloured links beside it were reading as the
           point of the column. Title AND aria-label — the tooltip is for the
           pointer, the label for everything else. */
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={`Edit ${sprint.name}`}
          aria-label={`Edit ${sprint.name}`}
          className="-m-1 flex-none rounded-md p-1 text-faint hover:bg-surface-3 hover:text-ink"
        >
          <Pencil size={13} strokeWidth={1.75} aria-hidden="true" />
        </button>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          Edit sprint
        </Button>
      )}
      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setConfirmDelete(false);
        }}
        title={`Edit ${sprint.name}`}
        description="Rename it, or set new dates. Moving the end date offers to move every later sprint with it, so the cadence stays intact."
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!changed || !datesValid || edit.isPending}
              onClick={() => edit.mutate()}
            >
              {edit.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <label className="flex min-w-0 flex-col gap-1">
              <span className="fs-11 text-muted">Name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full min-w-0 rounded-control border border-line bg-base px-2 py-1.5 fs-13 text-ink"
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="fs-11 text-muted">Starts</span>
              <input
                type="date"
                value={startsOn}
                onChange={(event) => setStartsOn(event.target.value)}
                className="w-full min-w-0 rounded-control border border-line bg-base px-2 py-1.5 fs-13 text-ink"
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="fs-11 text-muted">Ends</span>
              <input
                type="date"
                value={endsOn}
                min={startsOn}
                onChange={(event) => setEndsOn(event.target.value)}
                className="w-full min-w-0 rounded-control border border-line bg-base px-2 py-1.5 fs-13 text-ink"
              />
            </label>
          </div>
          {!datesValid && (
            <p className="fs-12 text-danger">A sprint needs a name, and cannot end before it starts.</p>
          )}

          {/* Length as a choice, not arithmetic.
              A sprint is one or two weeks in practice, and the two date
              pickers above made that something you worked out in your head —
              and got wrong whenever a holiday fell inside, because "two weeks"
              means ten working days, not fourteen calendar ones. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="fs-11 text-muted">Length</span>
            {([5, 10] as const).map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setEndsOn(endDateForWorkingDays(startsOn, days, holidays.dates))}
                className={cx(
                  'rounded-control border px-2.5 py-1 fs-12 font-semibold',
                  workingDays === days
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-base text-ink hover:bg-surface-2',
                )}
              >
                {days === 5 ? '1 week' : '2 weeks'}
              </button>
            ))}
            <span className="fs-12 text-muted numeric">
              {workingDays} working day{workingDays === 1 ? '' : 's'}
            </span>
          </div>

          {/* The warning worth having BEFORE the scope is locked. Once a
              sprint is committed, a lost day is a conversation; here it is
              just a date you can move. */}
          {lostDays.length > 0 && (
            <div className="rounded-lg border border-warn-border bg-warn-tint px-3 py-2.5">
              <p className="fs-12.5 font-semibold text-warn">
                {lostDays.length} holiday{lostDays.length === 1 ? '' : 's'} inside these dates
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {lostDays.map((holiday) => (
                  <li key={holiday.id} className="fs-12 text-muted">
                    {sprintDate(holiday.onDate, true)} — {holiday.name}
                    {holiday.estimated && ' (estimated; confirm the announced date)'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {startsOnIsNotAWorkingDay && (
            <p className="fs-12 text-warn">
              This starts on a day the team is off.{' '}
              <button
                type="button"
                onClick={() => setStartsOn(nextWorkingDay(startsOn, holidays.dates))}
                className="font-semibold underline"
              >
                Move it to {sprintDate(nextWorkingDay(startsOn, holidays.dates), true)}
              </button>
              .
            </p>
          )}

          {/* Asked the moment the dates change, rather than as a second dialog
              on top of this one after Save: the answer depends on dates that
              are still being picked, and naming the sprints that would move —
              here, while they can still be changed — is worth more than a
              confirmation after the fact. */}
          {shiftDays !== 0 && later.length > 0 && (
            <label className="flex items-start gap-2.5 rounded-lg border border-accent-border bg-accent-tint-soft px-3 py-2.5">
              <input
                type="checkbox"
                checked={cascade}
                onChange={(event) => setCascade(event.target.checked)}
                className="mt-[0.1875rem] flex-none accent-accent"
              />
              <span className="min-w-0">
                <span className="block fs-13 font-semibold text-ink">
                  Move the {later.length === 1 ? 'next sprint' : `next ${later.length} sprints`}{' '}
                  {shiftDays > 0 ? 'forward' : 'back'} by {Math.abs(shiftDays)}{' '}
                  {Math.abs(shiftDays) === 1 ? 'day' : 'days'} too
                </span>
                <span className="block fs-12 leading-snug text-muted">
                  {later.map((row) => row.name).join(', ')}. Without this they keep their current dates
                  {shiftDays > 0 ? ' and overlap this one' : ' and leave a gap'}.
                </span>
              </span>
            </label>
          )}

          {shiftDays !== 0 && later.length === 0 && (
            <p className="fs-12 text-muted">No later sprints to move.</p>
          )}

          {state === 'locked' ? (
            <div className="flex flex-col gap-1.5 border-t border-line pt-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="fs-13 font-semibold text-ink">Unlock this sprint</span>
                <Button size="sm" disabled={unlock.isPending} onClick={() => unlock.mutate()}>
                  {unlock.isPending ? 'Unlocking…' : 'Unlock'}
                </Button>
              </div>
              <p className="fs-12 text-muted">
                Puts it back to planning and drops the frozen scope taken at Start. For a sprint started by
                mistake — the clients whose requests went into it were already told it had started, and each
                one gets a trail entry saying it was unlocked.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 border-t border-line pt-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="fs-13 font-semibold text-ink">Delete this sprint</span>
                {confirmDelete ? (
                  <span className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate()}
                    >
                      {remove.isPending ? 'Deleting…' : 'Delete for good'}
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
                    Delete
                  </Button>
                )}
              </div>
              <p className="fs-12 text-muted">
                Only a sprint that has never started can be deleted. Anything placed in it goes back to the
                top of the backlog rather than being deleted with it.
              </p>
            </div>
          )}

          {error !== null && (
            <p role="alert" className="fs-12 text-danger">
              {error.message}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
